import { type ComponentProps, useState, type VoidFunctionComponent } from 'react';

import { getCookie, setCookie } from '../../../helpers/cookies';
import useOnMount from '../../../hooks/useOnMount';
import IconButton from '../Button/IconButton';
import { icons } from '../Icon';

import CardBanner, { CardBannerVariant } from './CardBanner';

import styles from './CardBanner.module.less';

type CardBannerProps = ComponentProps<typeof CardBanner>;

type Props = CardBannerProps & {
  /**
   * Cookie key used to persist the dismissed state.
   */
  dismissKey: string;
  /**
   * Number of days to persist the dismissal.
   * @default 365
   */
  persistDays?: number;
  /**
   * Optional callback fired after the banner is dismissed and persisted.
   */
  onDismissAfterPersist?: () => void;
};

/**
 * Wrapper around CardBanner that provides persistent dismissal via cookie.
 * Renders nothing if the cookie is set.
 */
const DismissibleCardBanner: VoidFunctionComponent<Props> = ({
  dismissKey,
  persistDays = 365,
  onDismissAfterPersist,
  ...cardBannerProps
}) => {
  const [isHidden, setIsHidden] = useState<boolean>(false);

  useOnMount(() => {
    const raw = getCookie(dismissKey);
    setIsHidden(raw === '1');
  });

  if (isHidden) {
    return null;
  }

  const { ...rest } = cardBannerProps;

  return (
    <div className={styles.dismissableCardBanner}>
      <CardBanner
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
      />
      <div className={styles.cardBannerDismiss}>
        <IconButton
          id="dismiss-card-banner"
          icon={icons.times}
          isDark={rest.variant === CardBannerVariant.PROMO}
          onClick={() => {
            setCookie(dismissKey, '1', persistDays);
            setIsHidden(true);
            onDismissAfterPersist?.();
          }}
        />
      </div>
    </div>
  );
};

export default DismissibleCardBanner;
export { CardBannerVariant } from './CardBanner';
